import type { IAmendmentPending } from "../../apis/invoice";
import { lineTotal } from "../../utils/invoice-totals";

export type LineChangeKind = "changed" | "added" | "removed";

export type LineChange = {
  key: string;
  kind: LineChangeKind;
  name: string;
  qtyFrom?: number;
  qtyTo?: number;
  priceFrom?: number;
  priceTo?: number;
  totalFrom?: number;
  totalTo?: number;
  /** Single-side qty for added/removed. */
  qty?: number;
  price?: number;
  delta: number;
};

/** Old values to render under proposal cells — null when that field did not change. */
export type ProposalWas = {
  quantity: number | null;
  price: number | null;
  total: number | null;
};

export type ProposalDisplayLine = {
  id: string;
  name: string;
  skuCode?: string | null;
  unit?: unknown;
  variantId?: string;
  quantity: number;
  price: number;
  discountAmount: number;
  totalAmount: number;
  _changeKey: string;
  _changeKind?: LineChangeKind;
  _was: ProposalWas | null;
  /** Full-row strike for removed lines. */
  _removed?: boolean;
  [key: string]: unknown;
};

function manualKey(name: string, price: number): string {
  return `m:${String(name).trim().toLowerCase()}|${Number(price) || 0}`;
}

function rowKey(li: any, isAdditional: boolean): string {
  if (!isAdditional && li?.variantId) return `v:${li.variantId}`;
  return manualKey(li?.name ?? "", Number(li?.price ?? 0));
}

type Side = {
  key: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  raw?: any;
};

/** Invoice line total — prefers stored lineTotalAmount, else price×qty−discount. */
export function invoiceLineTotal(li: any): number {
  for (const key of ["lineTotalAmount", "totalAmount", "total"] as const) {
    if (li?.[key] != null && li[key] !== "") {
      const n = Number(li[key]);
      if (Number.isFinite(n)) return n;
    }
  }
  return lineTotal(li);
}

function toSides(lines: any[], additional: any[]): Side[] {
  const out: Side[] = [];
  (lines ?? []).forEach((li, i) => {
    out.push({
      key: rowKey(li, false) || `l-${i}`,
      name: li?.name ?? li?.productName ?? li?.skuCode ?? "-",
      quantity: Number(li?.quantity ?? li?.qty ?? 0),
      price: Number(li?.price ?? li?.unitPrice ?? 0),
      total: invoiceLineTotal(li),
      raw: li,
    });
  });
  (additional ?? []).forEach((li, i) => {
    out.push({
      key: rowKey(li, true) || `a-${i}`,
      name: li?.name ?? "-",
      quantity: Number(li?.quantity ?? 0),
      price: Number(li?.price ?? 0),
      total: invoiceLineTotal(li),
      raw: li,
    });
  });
  return out;
}

function proposedSides(pending: IAmendmentPending, currentLines: any[]): Side[] {
  const nameByVariant = new Map<string, any>();
  (currentLines ?? []).forEach((li) => {
    if (li?.variantId) nameByVariant.set(li.variantId, li);
  });

  const out: Side[] = [];
  (pending.lines?.lines ?? []).forEach((li, i) => {
    const known = nameByVariant.get(li.variantId);
    const name =
      li.name ||
      known?.name ||
      known?.nameMon ||
      (li.skuCode ? `SKU ${li.skuCode}` : `SKU ${li.variantId}`);
    out.push({
      key: rowKey(li, false) || `pl-${i}`,
      name,
      quantity: Number(li.quantity ?? 0),
      price: Number(li.price ?? 0),
      total: lineTotal(li),
      raw: li,
    });
  });
  (pending.lines?.additionalLines ?? []).forEach((li, i) => {
    out.push({
      key: rowKey(li, true) || `pa-${i}`,
      name: li.name ?? "-",
      quantity: Number(li.quantity ?? 0),
      price: Number(li.price ?? 0),
      total: lineTotal(li),
      raw: li,
    });
  });
  return out;
}

/** Diff current invoice lines vs pending amendment payload. */
export function computeAmendmentLineChanges(
  pending: IAmendmentPending,
  currentLines: any[],
  currentAdditional: any[] = [],
): LineChange[] {
  const before = toSides(currentLines, currentAdditional);
  const after = proposedSides(pending, currentLines);
  const beforeMap = new Map(before.map((r) => [r.key, r]));
  const afterMap = new Map(after.map((r) => [r.key, r]));
  const keys = new Set<string>([
    ...Array.from(beforeMap.keys()),
    ...Array.from(afterMap.keys()),
  ]);
  const changes: LineChange[] = [];

  keys.forEach((key) => {
    const a = beforeMap.get(key);
    const b = afterMap.get(key);
    if (a && !b) {
      changes.push({
        key,
        kind: "removed",
        name: a.name,
        qty: a.quantity,
        price: a.price,
        totalFrom: a.total,
        delta: -a.total,
      });
      return;
    }
    if (!a && b) {
      changes.push({
        key,
        kind: "added",
        name: b.name,
        qty: b.quantity,
        price: b.price,
        totalTo: b.total,
        delta: b.total,
      });
      return;
    }
    if (a && b) {
      const qtyChanged = a.quantity !== b.quantity;
      const priceChanged = a.price !== b.price;
      const delta = b.total - a.total;
      if (!qtyChanged && !priceChanged && Math.abs(delta) < 0.005) return;
      changes.push({
        key,
        kind: "changed",
        name: b.name || a.name,
        qtyFrom: a.quantity,
        qtyTo: b.quantity,
        priceFrom: a.price,
        priceTo: b.price,
        totalFrom: a.total,
        totalTo: b.total,
        delta,
      });
    }
  });

  return changes.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
}

function wasForChange(ch: LineChange | undefined): ProposalWas | null {
  if (!ch || ch.kind !== "changed") return null;
  const qtyChanged = ch.qtyFrom !== ch.qtyTo;
  const priceChanged = ch.priceFrom !== ch.priceTo;
  const totalChanged =
    ch.totalFrom != null && ch.totalTo != null && Math.abs(ch.totalFrom - ch.totalTo) > 0.005;
  if (!qtyChanged && !priceChanged && !totalChanged) return null;
  return {
    quantity: qtyChanged ? (ch.qtyFrom ?? null) : null,
    price: priceChanged ? (ch.priceFrom ?? null) : null,
    total: totalChanged ? (ch.totalFrom ?? null) : null,
  };
}

/** Build table rows for the «Санал» line view (proposal + removed). */
export function buildProposalDisplayLines(
  pending: IAmendmentPending,
  currentLines: any[],
  currentAdditional: any[] = [],
): ProposalDisplayLine[] {
  const changes = computeAmendmentLineChanges(pending, currentLines, currentAdditional);
  const changeByKey = new Map(changes.map((c) => [c.key, c]));
  const nameByVariant = new Map<string, any>();
  (currentLines ?? []).forEach((li) => {
    if (li?.variantId) nameByVariant.set(li.variantId, li);
  });

  const rows: ProposalDisplayLine[] = (pending.lines?.lines ?? []).map((li: any, i: number) => {
    const known = nameByVariant.get(li.variantId);
    const key = lineChangeKeyForInvoiceLine(li, false);
    const ch = changeByKey.get(key);
    const qty = Number(li.quantity ?? 0);
    const price = Number(li.price ?? 0);
    const disc = Number(li.discountAmount ?? li.discount ?? 0);
    return {
      ...li,
      id: `p-${li.variantId}-${i}`,
      name: li.name || known?.name || known?.nameMon || li.skuCode || "-",
      skuCode: li.skuCode ?? known?.skuCode,
      unit: known?.unit,
      quantity: qty,
      price,
      discountAmount: disc,
      totalAmount: lineTotal(li),
      _changeKey: key,
      _changeKind: ch?.kind,
      _was: wasForChange(ch),
      _removed: false,
    };
  });

  changes
    .filter((c) => c.kind === "removed")
    .forEach((c, i) => {
      const before = toSides(currentLines, currentAdditional).find((s) => s.key === c.key);
      const raw = before?.raw ?? {};
      rows.push({
        ...raw,
        id: `r-${c.key}-${i}`,
        name: c.name,
        quantity: c.qty ?? 0,
        price: c.price ?? 0,
        discountAmount: Number(raw.discountAmount ?? 0),
        totalAmount: c.totalFrom ?? 0,
        _changeKey: c.key,
        _changeKind: "removed",
        _was: null,
        _removed: true,
      });
    });

  return rows;
}

export function proposedLineKeys(pending: IAmendmentPending, currentLines: any[]): Set<string> {
  return new Set(proposedSides(pending, currentLines).map((r) => r.key));
}

export function lineChangeKeyForInvoiceLine(li: any, isAdditional = false): string {
  return rowKey(li, isAdditional);
}
